import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, FormControl, Select, MenuItem } from '@mui/material';
import {
  Plus, Pencil, Trash2, Search, Briefcase, X, ChevronRight, Clock,
  Library, Code2, ListChecks, MessageSquare, Lightbulb, HelpCircle, Filter,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../../api/client';
import Pagination from '../../component/Pagination';
import NotificationPopup from '../../component/NotificationPopup';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import PageHeader, { PageBody, PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import QuestionBankFormModal from './QuestionBankFormModal';
import {
  emptyQuestionForm,
  formFromBankItem,
  getQuestionTypeChipStyles,
  getQuestionTypeOptions,
  getMcqChoices,
  isQuestionFormValid,
  payloadFromForm,
} from './questionBankUtils';

const getJdStatusStyle = (status) => {
  const styles = {
    open: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-700',
    expired: 'bg-amber-100 text-amber-800',
    archived: 'bg-slate-100 text-slate-600',
  };
  return styles[status] || 'bg-gray-100 text-gray-700';
};

const THEME_GRADIENT = { background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' };

const themeButtonClass =
  'inline-flex items-center gap-1.5 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-[0_8px_16px_-10px_rgba(217,70,239,0.85)] hover:shadow-[0_12px_22px_-10px_rgba(14,165,233,0.5)] hover:brightness-[1.05] active:scale-[0.98] transition-all cursor-pointer';

const TYPE_META = {
  concept: {
    icon: Lightbulb,
    wrap: 'bg-violet-50 text-violet-600 ring-1 ring-violet-100',
    bar: 'bg-gradient-to-b from-violet-400 to-purple-600',
    wash: 'bg-[linear-gradient(180deg,#faf5ff_0%,#ffffff_42%)]',
    border: 'border-violet-100',
    pill: 'bg-violet-50 text-violet-700 ring-1 ring-violet-100',
    glow: 'bg-[radial-gradient(90%_120%_at_0%_50%,rgba(139,92,246,0.16),transparent_55%)]',
    hover: 'hover:border-violet-300 hover:shadow-[0_18px_36px_-16px_rgba(139,92,246,0.45)]',
  },
  coding: {
    icon: Code2,
    wrap: 'bg-orange-50 text-orange-600 ring-1 ring-orange-100',
    bar: 'bg-gradient-to-b from-orange-400 to-amber-500',
    wash: 'bg-[linear-gradient(180deg,#fff7ed_0%,#ffffff_42%)]',
    border: 'border-orange-100',
    pill: 'bg-orange-50 text-orange-700 ring-1 ring-orange-100',
    glow: 'bg-[radial-gradient(90%_120%_at_0%_50%,rgba(249,115,22,0.16),transparent_55%)]',
    hover: 'hover:border-orange-300 hover:shadow-[0_18px_36px_-16px_rgba(249,115,22,0.42)]',
  },
  mcq: {
    icon: ListChecks,
    wrap: 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100',
    bar: 'bg-gradient-to-b from-emerald-400 to-teal-500',
    wash: 'bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_42%)]',
    border: 'border-emerald-100',
    pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    glow: 'bg-[radial-gradient(90%_120%_at_0%_50%,rgba(16,185,129,0.16),transparent_55%)]',
    hover: 'hover:border-emerald-300 hover:shadow-[0_18px_36px_-16px_rgba(16,185,129,0.42)]',
  },
  behavioral: {
    icon: MessageSquare,
    wrap: 'bg-pink-50 text-pink-600 ring-1 ring-pink-100',
    bar: 'bg-gradient-to-b from-pink-400 to-fuchsia-500',
    wash: 'bg-[linear-gradient(180deg,#fdf2f8_0%,#ffffff_42%)]',
    border: 'border-pink-100',
    pill: 'bg-pink-50 text-pink-700 ring-1 ring-pink-100',
    glow: 'bg-[radial-gradient(90%_120%_at_0%_50%,rgba(236,72,153,0.16),transparent_55%)]',
    hover: 'hover:border-pink-300 hover:shadow-[0_18px_36px_-16px_rgba(236,72,153,0.42)]',
  },
  general: {
    icon: HelpCircle,
    wrap: 'bg-sky-50 text-sky-600 ring-1 ring-sky-100',
    bar: 'bg-gradient-to-b from-sky-400 to-fuchsia-500',
    wash: 'bg-[linear-gradient(180deg,#f0f9ff_0%,#ffffff_58%,#fdf4ff_100%)]',
    border: 'border-sky-100',
    pill: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
    glow: 'bg-[radial-gradient(90%_120%_at_0%_50%,rgba(14,165,233,0.16),transparent_55%)]',
    hover: 'hover:border-sky-300 hover:shadow-[0_18px_36px_-16px_rgba(14,165,233,0.42)]',
  },
};



export default function QuestionBank({ embedded = false, triggerCreate = 0 }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const questionTypeOptions = getQuestionTypeOptions(t);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoading(loading, { message: t('questionBank.loading', 'Loading questions…') });
  const [search, setSearch] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_count: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form, setForm] = useState(emptyQuestionForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [usageQuestion, setUsageQuestion] = useState(null);
  const [usageList, setUsageList] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const isSuperAdmin = userDetails?.is_superuser === true;
  const isCompanyAdmin = Array.isArray(userDetails?.admin_companies) && userDetails.admin_companies.length > 0;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      if (questionType) params.question_type = questionType;
      if (selectedCompany) params.company = selectedCompany;
      const response = await api.get('question-bank/', { params });
      const results = Array.isArray(response.data) ? response.data : (response.data.results || []);
      const count = response.data?.count ?? results.length;
      setQuestions(results);
      setPagination({
        current_page: page,
        total_pages: Math.max(1, Math.ceil(count / pageSize)),
        total_count: count,
      });
    } catch (error) {
      setQuestions([]);
      showSnackbar(t('questionBank.loadFailed', 'Failed to load Question Bank.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, questionType, selectedCompany, t]);

  useEffect(() => {
    if (!isSuperAdmin && !isCompanyAdmin) {
      navigate('/dashboard');
    }
  }, [isSuperAdmin, isCompanyAdmin, navigate]);

  useEffect(() => {
    api.get('companies/')
      .then((res) => {
        const companiesData = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setCompanies(companiesData);
        if (!isSuperAdmin && userDetails.admin_companies?.length === 1) {
          const match = companiesData.find((company) => userDetails.admin_companies.includes(company.name));
          if (match) setSelectedCompany(String(match.id));
        }
      })
      .catch(() => setCompanies([]));
  }, [isSuperAdmin, userDetails.admin_companies]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    if (triggerCreate > 0) {
      openCreate();
    }
  }, [triggerCreate]);

  const openCreate = () => {
    setEditingQuestion(null);
    setForm(emptyQuestionForm());
    setFormOpen(true);
  };

  const openEdit = (question) => {
    setEditingQuestion(question);
    setForm(formFromBankItem(question));
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!isQuestionFormValid(form)) return;
    if (isSuperAdmin && !selectedCompany && !editingQuestion) {
      showSnackbar(t('questionBank.companyRequired', 'Select a company before adding a question.'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (selectedCompany) payload.company = Number(selectedCompany);
      if (editingQuestion) {
        await api.patch(`question-bank/${editingQuestion.id}/`, payload);
        showSnackbar(t('questionBank.updated', 'Question updated.'));
      } else {
        await api.post('question-bank/', payload);
        showSnackbar(t('questionBank.created', 'Question added to the Question Bank.'));
      }
      setFormOpen(false);
      fetchQuestions();
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.choices || t('questionBank.saveFailed', 'Failed to save question.');
      showSnackbar(typeof detail === 'string' ? detail : t('questionBank.saveFailed', 'Failed to save question.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = async (question) => {
    try {
      const response = await api.get(`question-bank/${question.id}/usage/`);
      setDeleteTarget({
        ...question,
        used_in_count: response.data?.used_in_count || 0,
        job_descriptions: response.data?.job_descriptions || [],
      });
    } catch (error) {
      setDeleteTarget({ ...question, used_in_count: question.used_in_count || 0, job_descriptions: [] });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const used = (deleteTarget.used_in_count || 0) > 0;
      await api.delete(`question-bank/${deleteTarget.id}/`, {
        params: used ? { force: true } : undefined,
      });
      showSnackbar(t('questionBank.deleted', 'Question deleted.'));
      setDeleteTarget(null);
      fetchQuestions();
    } catch (error) {
      if (error.response?.status === 409) {
        setDeleteTarget({
          ...deleteTarget,
          used_in_count: error.response.data.used_in_count,
          job_descriptions: error.response.data.job_descriptions || [],
        });
      } else {
        showSnackbar(t('questionBank.deleteFailed', 'Failed to delete question.'), 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const openUsage = async (question) => {
    setUsageQuestion(question);
    setUsageLoading(true);
    try {
      const response = await api.get(`question-bank/${question.id}/usage/`);
      setUsageList(response.data?.job_descriptions || []);
    } catch (error) {
      setUsageList([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const closeUsage = () => {
    setUsageQuestion(null);
    setUsageList([]);
  };

  const deleteMessage = deleteTarget?.used_in_count > 0
    ? t(
      'questionBank.deleteInUseMessage',
      'This question is currently being used in {{count}} JDs/interviews. Deleting it will also remove it from those JDs. Are you sure you want to continue?',
      { count: deleteTarget.used_in_count }
    )
    : t('questionBank.deleteConfirm', 'Are you sure you want to delete this question?');

  const usageCount = usageLoading
    ? (Number(usageQuestion?.used_in_count) || 0)
    : usageList.length;
  const hasFilters = Boolean(search.trim() || questionType || selectedCompany);

  const bankBody = (
    <div className={`flex flex-col flex-1 min-h-0 ${embedded ? 'bg-slate-50/80' : 'bg-white'}`}>
      {/* Filter and Action Bar */}
      <div className="p-3 sm:p-3.5 border-b border-slate-200/80 bg-white flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm bg-white shadow-2xs h-[40px]"
              placeholder={t('questionBank.searchPlaceholder', 'Search questions...')}
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            />
          </div>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 160, sm: 190 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontSize: '0.8125rem',
                backgroundColor: 'white',
                height: '40px',
              },
            }}
          >
            <Select
              value={questionType}
              onChange={(e) => { setPage(1); setQuestionType(e.target.value); }}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                    mt: 0.5,
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>{t('questionBank.allTypes', 'All types')}</MenuItem>
              {questionTypeOptions.map((type) => (
                <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8125rem' }}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {companies.length > 1 && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 160, sm: 190 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontSize: '0.8125rem',
                  backgroundColor: 'white',
                  height: '40px',
                },
              }}
            >
              <Select
                value={selectedCompany}
                onChange={(e) => { setPage(1); setSelectedCompany(e.target.value); }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                    },
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>{t('jobDescription.allCompanies', 'All companies')}</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id} sx={{ fontSize: '0.8125rem' }}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      <div className="p-3.5 sm:p-4.5 flex-1 min-h-0 flex flex-col justify-between">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border">
            <CircularProgress size={40} sx={{ color: '#0ea5e9' }} />
            <p className="mt-3 text-gray-600 text-sm font-medium">{t('questionBank.loading', 'Loading questions...')}</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-2xl shadow-sm">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-50 to-fuchsia-50 ring-1 ring-sky-100">
              <Library className="h-7 w-7 text-sky-500" />
            </div>
            <p className="text-base text-slate-700 font-medium">{t('questionBank.empty', 'No questions in the Question Bank yet.')}</p>
            <button
              type="button"
              onClick={openCreate}
              style={THEME_GRADIENT}
              className={`mt-4 ${themeButtonClass}`}
            >
              <Plus size={16} className="stroke-[2.8]" />
              <span>{t('questionBank.addQuestion', 'Add Question')}</span>
            </button>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-between min-h-full">
            <div className="grid grid-cols-1 gap-3">
              {questions.map((question) => {
                const mcqChoices = question.question_type === 'mcq' ? getMcqChoices(question) : [];
                const usedCount = Number(question.used_in_count) || 0;
                const isUsed = usedCount > 0;
                const meta = TYPE_META[question.question_type] || TYPE_META.general;
                const TypeIcon = meta.icon;
                const typeLabel = questionTypeOptions.find((opt) => opt.value === question.question_type)?.label || question.question_type;
                return (
                  <article
                    key={question.id}
                    className={`group relative min-w-0 overflow-hidden rounded-2xl border ${meta.border} ${meta.wash} shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 hover:-translate-y-0.5 flex ${meta.hover}`}
                  >
                    <div className={`w-1.5 shrink-0 ${meta.bar} transition-all duration-200 group-hover:w-2.5`} />
                    <div className={`pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${meta.glow}`} />
                    <div className="relative z-[1] min-w-0 flex-1 p-3.5 sm:p-4">
                    <div className="flex items-start justify-between gap-3 mb-2.5">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105 ${meta.wrap}`}>
                          <TypeIcon size={18} strokeWidth={2.1} />
                        </div>
                        <div className="flex flex-wrap items-center gap-2 min-w-0 pt-1">
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide ${meta.pill}`}>
                          {typeLabel}
                        </span>

                        {question.question_type === 'coding' && (
                          <span className="text-[11px] text-orange-700 bg-white/80 border border-orange-200 px-2 py-0.5 rounded-md font-semibold flex items-center gap-1">
                            <Clock size={12} className="shrink-0" />
                            {question.time_limit || 5} min
                          </span>
                        )}

                        {question.company_name && (
                          <span className="text-[11px] text-slate-500 font-medium bg-white/80 border border-slate-200 px-2 py-0.5 rounded-md">
                            {question.company_name}
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => openUsage(question)}
                          className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold transition-all cursor-pointer ${isUsed
                              ? 'bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200/80'
                              : 'bg-white/80 text-slate-500 hover:bg-slate-50 border border-slate-200'
                            }`}
                          title={isUsed
                            ? t('questionBank.viewUsage', 'View linked job descriptions')
                            : t('questionBank.notUsed', 'This question is not used in any JDs.')}
                        >
                          <Briefcase size={12} className={isUsed ? 'text-sky-600' : 'text-slate-400'} />
                          {isUsed ? (
                            <>
                              <span>{t('questionBank.usedInLabel', 'Used in')}</span>
                              <span className="inline-flex min-w-[1.125rem] h-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white" style={THEME_GRADIENT}>
                                {usedCount}
                              </span>
                              <span>
                                {usedCount === 1
                                  ? t('questionBank.jdSingular', 'JD')
                                  : t('questionBank.jdPlural', 'JDs')}
                              </span>
                            </>
                          ) : (
                            <span>{t('questionBank.notUsedShort', 'Not used in any JDs')}</span>
                          )}
                        </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => openEdit(question)}
                          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-400 transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600 cursor-pointer"
                          title={t('common.edit', 'Edit')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDelete(question)}
                          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-400 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 cursor-pointer"
                          title={t('common.delete', 'Delete')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    <div className="text-sm text-slate-900 font-medium whitespace-pre-wrap leading-relaxed pl-[52px]">
                      {question.text}
                    </div>

                    {/* MCQ Choices (if applicable, compact grid) */}
                    {question.question_type === 'mcq' && (
                      <div className="mt-2.5 pl-[52px]">
                        {mcqChoices.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">
                            {t('questionBank.noMcqOptions', 'No multiple choice options saved for this question.')}
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {mcqChoices.map((choice, cIdx) => (
                              <div
                                key={`${question.id}-choice-${cIdx}`}
                                className={`flex items-center gap-2 text-xs rounded-lg px-2.5 py-1 border ${choice.is_correct
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-semibold'
                                    : 'bg-gray-50 border-gray-200 text-gray-700'
                                  }`}
                              >
                                <span className="text-gray-500 font-bold w-4 shrink-0 text-[10px]">
                                  {String.fromCharCode(65 + cIdx)}.
                                </span>
                                <span className={`flex-1 min-w-0 truncate ${choice.is_correct ? 'text-emerald-900 font-semibold' : 'text-gray-700'}`}>
                                  {choice.text}
                                </span>
                                {choice.is_correct && (
                                  <span className="px-1.5 py-0.2 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold shrink-0">
                                    {t('questionBank.correct', 'Correct')}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="mt-auto pt-4 pb-1 border-t border-gray-200/80 bg-white/80 rounded-xl px-2 sm:px-4 shadow-2xs">
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                currentCount={questions.length}
                onPageChange={setPage}
                pageSize={pageSize}
                forceVisible
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />

      {embedded ? (
        <div className="flex flex-col h-full">
          {bankBody}
        </div>
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30" style={{ paddingTop: '10px' }}>
          <div className="w-full max-w-[1920px] mx-auto px-4 py-3 sm:px-6 lg:px-8 h-full">
            <div className="bg-white rounded-xl flex flex-col h-full overflow-hidden shadow-xs border border-gray-200/80">
              <div
                className="px-4 py-3 text-white rounded-t-xl shadow-xs"
                style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h1 className="text-lg font-semibold mb-0.5 text-white">{t('questionBank.title', 'Question Bank')}</h1>
                    <p className="text-xs text-blue-100">{t('questionBank.subtitle', 'Create and reuse interview questions for your company')}</p>
                  </div>
                  <button
                    onClick={openCreate}
                    className="flex items-center gap-1.5 bg-white text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors shadow-xs text-xs font-semibold cursor-pointer"
                  >
                    <Plus size={14} className="stroke-[2.5]" />
                    {t('questionBank.addQuestion', 'Add Question')}
                  </button>
                </div>
              </div>
              {bankBody}
            </div>
          </div>
        </div>
      )}

      <QuestionBankFormModal
        open={formOpen}
        editingQuestion={editingQuestion}
        form={form}
        setForm={setForm}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      {usageQuestion && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999]"
          onClick={closeUsage}
        >
          <div
            className="bg-white rounded-[20px] w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            style={{ boxShadow: '0 25px 50px -12px rgba(14, 165, 233, 0.25)' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="px-6 py-5 text-white relative shrink-0"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
            >
              <div className="flex items-start gap-3 pr-10">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)' }}
                >
                  <Briefcase size={20} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-white leading-tight">
                    {t('questionBank.usedIn', 'Used In')}
                  </h3>
                  <p className="text-xs text-white/90 mt-0.5">
                    {t('questionBank.usedInSubtitle', 'Job descriptions linked to this question')}
                  </p>
                  <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/20 border border-white/35 px-2.5 py-1 text-xs font-semibold text-white">
                    <span className="inline-flex min-w-[1.15rem] h-[18px] items-center justify-center rounded-full bg-white text-sky-600 px-1 text-[11px] font-bold">
                      {usageCount}
                    </span>
                    <span>
                      {usageCount === 1
                        ? t('questionBank.jdSingular', 'JD')
                        : t('questionBank.jdPlural', 'JDs')}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeUsage}
                className="absolute top-3 right-3 p-1.5 rounded-lg text-white hover:bg-white/20 transition-colors"
                aria-label={t('common.close', 'Close')}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {usageQuestion.text && (
                <div className="mb-4 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5">
                  <p className="text-sm text-gray-700 font-medium line-clamp-3 whitespace-pre-wrap">
                    {usageQuestion.text}
                  </p>
                </div>
              )}

              {usageLoading ? (
                <div className="py-10 flex flex-col items-center justify-center gap-3">
                  <CircularProgress size={28} sx={{ color: '#0ea5e9' }} />
                  <p className="text-sm text-gray-500">{t('questionBank.loadingUsage', 'Loading linked JDs...')}</p>
                </div>
              ) : usageList.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center mb-3">
                    <Briefcase size={22} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">
                    {t('questionBank.notUsedShort', 'Not used in any JDs')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                    {t('questionBank.emptyUsageHint', 'This question is not linked to any job descriptions yet.')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {usageList.map((jd) => (
                    <li key={jd.id}>
                      <button
                        type="button"
                        className="group w-full text-left flex items-center gap-3 p-3.5 rounded-2xl border border-gray-200 hover:border-sky-300 hover:bg-sky-50/70 hover:shadow-sm transition-all"
                        onClick={() => navigate('/jobdescriptions')}
                      >
                        <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0 group-hover:bg-sky-100">
                          <Briefcase size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{jd.title}</div>
                          <span className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${getJdStatusStyle(jd.status)}`}>
                            {t(`jobDescription.${jd.status}`, jd.status)}
                          </span>
                        </div>
                        <ChevronRight size={18} className="text-gray-300 group-hover:text-sky-500 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              className="px-6 py-4 flex justify-end border-t border-slate-100 shrink-0"
              style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}
            >
              <button
                type="button"
                onClick={closeUsage}
                className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all"
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('questionBank.deleteTitle', 'Delete Question')}
        message={deleteMessage}
        confirmText={deleteTarget?.used_in_count > 0
          ? t('questionBank.deleteAnyway', 'Delete Anyway')
          : t('common.delete', 'Delete')}
      />
    </>
  );
}
